/**
 * API utility for making authenticated requests to the backend.
 * Handles token refresh on 401 responses automatically.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Turn a snake_case field key into a readable Title Case label.
 *  Pure array indices (e.g. "0", "1") are not meaningful labels, so they
 *  are dropped — otherwise list validation errors render as "0: ...". */
function humanizeFieldKey(key) {
  if (!key) return '';
  const last = key.split('.').pop();
  if (/^\d+$/.test(last)) return '';
  return last
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Strip framework noise (e.g. Pydantic's "Value error, " prefix) from a message. */
function cleanValidationMessage(msg) {
  return String(msg ?? '').replace(/^value error,\s*/i, '').trim();
}

/** Extract a human-readable message (and structured detail) from a non-ok Response */
async function extractErrorPayload(response) {
  const error = await response.json().catch(() => ({}));
  const detail = error.detail;
  let message = '';
  if (Array.isArray(detail)) {
    message = detail.map((e) => {
      if (!e || typeof e !== 'object') return cleanValidationMessage(e);
      const rawLoc = Array.isArray(e.loc)
        ? e.loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path').join('.')
        : '';
      const label = humanizeFieldKey(rawLoc);
      const msg = cleanValidationMessage(e.msg || e.message || JSON.stringify(e));
      return label ? `${label}: ${msg}` : msg;
    }).filter(Boolean).join('; ');
  } else if (typeof detail === 'string' && detail) {
    message = detail;
  } else if (detail && typeof detail === 'object') {
    message = detail.msg || detail.message || detail.detail || JSON.stringify(detail);
  } else if (error.message) {
    message = error.message;
  }
  if (!message) {
    message = `API Error: ${response.status} ${response.statusText || ''}`.trim();
  }
  return { message, detail, body: error, status: response.status };
}

/** Wrap a fetch call so network-level failures get a clear message */
async function safeFetch(url, options) {
  try {
    return await fetch(url, options);
  } catch (err) {
    // Let AbortError propagate untouched so callers can detect cancellation
    if (err.name === 'AbortError') throw err;
    // Some browsers surface abort as TypeError("Failed to fetch") instead of AbortError.
    // Check if the signal was aborted — if so, treat it as cancellation, not a network error.
    if (err instanceof TypeError && options?.signal?.aborted) {
      const abortErr = new DOMException('The operation was aborted.', 'AbortError');
      throw abortErr;
    }
    // Log full details for debugging intermittent failures
    console.error('[API] Network error:', {
      url,
      method: options?.method || 'GET',
      error: err.message,
      type: err.constructor.name,
      timestamp: new Date().toISOString(),
    });
    // Genuine network failure
    if (err instanceof TypeError) {
      throw new Error(
        `Unable to reach the server (${err.message}). Check your network connection or try again.`
      );
    }
    throw err;
  }
}

/** Singleton refresh promise to prevent concurrent refresh attempts */
let _refreshPromise = null;

export const api = {
  baseUrl: API_URL,

  async _refreshToken() {
    // If a refresh is already in-flight, wait for it instead of firing another
    if (_refreshPromise) {
      console.info('[API] Refresh already in-flight, waiting...');
      return _refreshPromise;
    }

    _refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;
      try {
        console.info('[API] Refreshing access token...');
        const resp = await safeFetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!resp.ok) {
          console.error('[API] Token refresh failed with status:', resp.status);
          return false;
        }
        const data = await resp.json();
        localStorage.setItem('token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        if (data.tenant) localStorage.setItem('tenant', JSON.stringify(data.tenant));
        console.info('[API] Token refreshed successfully');
        return true;
      } catch (err) {
        console.error('[API] Token refresh threw:', err.message);
        return false;
      }
    })();

    try {
      return await _refreshPromise;
    } finally {
      _refreshPromise = null;
    }
  },

  async _fetch(endpoint, options = {}, _retryCount = 0) {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': token ? `Bearer ${token}` : '',
      ...options.headers,
    };
    if (!options.skipContentType) headers['Content-Type'] = 'application/json';

    // If already aborted before we even start, bail immediately
    if (options.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    let response;
    try {
      response = await safeFetch(`${API_URL}${endpoint}`, { ...options, headers });
    } catch (err) {
      // Don't retry aborted requests
      if (err.name === 'AbortError') throw err;
      // Retry once on transient network errors (connection reset, etc.)
      if (_retryCount < 1) {
        console.warn(`[API] Retrying after network error: ${endpoint}`, err.message);
        await new Promise(r => setTimeout(r, 500));
        return this._fetch(endpoint, options, _retryCount + 1);
      }
      throw err;
    }

    if (response.status === 401) {
      console.warn(`[API] 401 on ${endpoint}, attempting token refresh...`);
      const refreshed = await this._refreshToken();
      if (refreshed) {
        console.info(`[API] Token refreshed, retrying: ${endpoint}`);
        const newToken = localStorage.getItem('token');
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await safeFetch(`${API_URL}${endpoint}`, { ...options, headers });
      } else {
        console.error(`[API] Token refresh failed, redirecting to login`);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
    }

    // Log non-ok responses for debugging
    if (!response.ok) {
      console.warn(`[API] ${response.status} on ${options.method || 'GET'} ${endpoint}`);
    }

    return response;
  },

  async _handleResponse(response) {
    if (!response.ok) {
      const payload = await extractErrorPayload(response);
      const err = new Error(payload.message);
      err.status = payload.status;
      err.detail = payload.detail;
      err.body = payload.body;
      throw err;
    }
    try {
      return await response.json();
    } catch (err) {
      // AbortError can fire during json() parsing if request was cancelled
      if (err.name === 'AbortError') throw err;
      throw new Error('Failed to parse server response');
    }
  },

  async get(endpoint, options = {}) {
    const response = await this._fetch(endpoint, options);
    return this._handleResponse(response);
  },

  async post(endpoint, data) {
    const response = await this._fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this._handleResponse(response);
  },

  /** Multipart POST (do not set Content-Type — browser sets boundary). */
  async postForm(endpoint, formData) {
    const response = await this._fetch(endpoint, {
      method: 'POST',
      body: formData,
      skipContentType: true,
    });
    return this._handleResponse(response);
  },

  async put(endpoint, data) {
    const response = await this._fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this._handleResponse(response);
  },

  async delete(endpoint) {
    const response = await this._fetch(endpoint, { method: 'DELETE' });
    return this._handleResponse(response);
  },

  async patch(endpoint, data) {
    const response = await this._fetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return this._handleResponse(response);
  },

  /** Partial Payment endpoints (loan-scoped, tenant-scoped). */
  partialPayments: {
    eligibility(loanId) {
      return api.get(`/api/loans/${loanId}/partial-payments/eligibility`);
    },
    preview(loanId, body) {
      return api.post(`/api/loans/${loanId}/partial-payments/preview`, body);
    },
    async create(loanId, body, idempotencyKey) {
      const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
      const response = await api._fetch(`/api/loans/${loanId}/partial-payments`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      });
      return api._handleResponse(response);
    },
    list(loanId) {
      return api.get(`/api/loans/${loanId}/partial-payments`);
    },
    get(loanId, id) {
      return api.get(`/api/loans/${loanId}/partial-payments/${id}`);
    },
  },
};
